/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Label, Checkbox, Dialog, LoadingButton } from '@/component'
import { createContext, useContext, useState } from 'react'
import { useBatch } from '@/hook/use-batch'
import { CheckboxCard } from '@/bc/card/checkbox-card'
import { DialogTrigger, DialogContent, DialogHeader, DialogDescription, DialogFooter } from '@/component'
import { useRequest } from 'ahooks'
import { postFabricAssociationCancel } from '@/service/fabric-api'
import { toast } from 'sonner'
type BatchContextType<T> = ReturnType<typeof useBatch<T>>
const BatchContext = createContext<BatchContextType<any>>({
  selected: [],
  noneSelected: true,
  allSelected: false,
  partiallySelected: false,
  setSelected: () => {},
  isSelected: () => false,
  select: () => {},
  unSelect: () => {},
  toggle: () => {},
  toggleAll: () => {},
  setBatchMode: () => {},
  batchMode: false,
  selectAll: () => {},
  unSelectAll: () => {},
  clearAll: () => {},
})
type BatchProps<T> = {
  children: React.ReactNode
  defaultBatchMode?: boolean
  items: T[]
  maxCount?: number
  itemKey: keyof T | ((item: T) => React.Key) | undefined
}
export function Batch<T>({ children, items, itemKey, defaultBatchMode, maxCount }: BatchProps<T>) {
  const batch = useBatch(items, {
    itemKey,
    defaultBatchMode,
    maxCount,
  })
  return <BatchContext.Provider value={batch}>{children}</BatchContext.Provider>
}
export function BatchTrigger() {
  const { setBatchMode } = useBatchContext()
  return (
    <Button variant="outline" className="w-20" onClick={() => setBatchMode(true)}>
      批量操作
    </Button>
  )
}
export function useBatchContext() {
  return useContext(BatchContext)
}
export function BatchItem<T>({
  item,
  children,
  position,
  className,
}: {
  item: T
  children: React.ReactNode
  position?: 'left' | 'right'
  className?: string
}) {
  const { batchMode, toggle, isSelected, setBatchMode } = useBatchContext()
  return (
    <CheckboxCard
      className={className}
      position={position}
      isBatchMode={batchMode}
      checked={isSelected(item)}
      onCheckedChange={() => {
        toggle(item)
        setBatchMode(true)
      }}
    >
      {children}
    </CheckboxCard>
  )
}
export function BatchOperation({ children }: { children: React.ReactNode }) {
  const { allSelected, batchMode, toggleAll, selected, setBatchMode } = useBatchContext()
  if (!batchMode) return null
  return (
    <div className="flex justify-between">
      <span className="flex items-center gap-4">
        <Label>
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
          <span>全选</span>
        </Label>
        <span>已选{selected.length}个</span>
      </span>
      <span className="flex items-center gap-4">
        {children}
        <Button variant="outline" onClick={() => setBatchMode(false)}>
          取消
        </Button>
      </span>
    </div>
  )
}
type AssociateBatchOperationProps<T> = {
  toModel: (item: T) => { sourceChannel: number; entityIdList: string[] }
}
export function UnassociatedBatchOperation<T>({
  fabricId,
  toModel,
  onSuccess,
}: AssociateBatchOperationProps<T> & {
  fabricId: string
  onSuccess?: () => void
}) {
  const [open, setOpen] = useState(false)
  const { selected } = useBatchContext() as BatchContextType<T>
  const { run, loading } = useRequest(
    async () => {
      await postFabricAssociationCancel({
        associateInfoList: selected.map((item) => toModel(item)),
        fabricId: fabricId,
      })
    },
    {
      manual: true,
      onSuccess: () => {
        setOpen(false)
        toast.success('取消供应成功')
        onSuccess?.()
      },
    },
  )
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">取消供应</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>取消供应</DialogHeader>
        <DialogDescription>确定取消供应吗？</DialogDescription>
        <DialogFooter className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => setOpen(false)}>
            取消
          </Button>
          <LoadingButton loading={loading} variant="default" onClick={() => run()}>
            确定
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
export function Unbatch(props: React.ComponentProps<'div'>) {
  const { batchMode } = useBatchContext()
  if (batchMode) return null
  return <div {...props} />
}
